// A routine's schedule, said in words — and its activity, said honestly.
//
// Two pure concerns, deliberately kept in one import-free module so both can be unit tested without
// dragging the app's dependency graph in (see CLAUDE.md → "Tests (jest)").
//
//   1. `cronLabel` turns the entity's raw 5-field cron into "Every day · 9:00 AM". It is the web mirror
//      of iOS `RoutineScheduleLabel.cronLabel` / Android's `pulseFrequencyLabel`, down to the shapes it
//      recognises, so the same routine reads the same on all three surfaces. Copy is passed IN rather
//      than looked up here, which is what keeps the module import-free and locale-correct.
//   2. `routineActivity` decides what the card may say about runs — and, crucially, when it must say
//      NOTHING. See below.

/** The five schedule phrasings a cron can resolve to. Supplied by the caller, already translated. */
export interface CronLabelStrings {
  /** "Every day" */
  everyDay: string;
  /** "Weekdays" */
  weekdays: string;
  /** "Weekends" */
  weekends: string;
  /** "Every {day}" — `{day}` is replaced with the weekday's own name. */
  everyDow: string;
  /** "Day {days} of the month" — `{days}` is replaced with the day numbers, comma-joined. */
  dayOfMonth: string;
}

const DOW_NAMES: Record<string, number> = {
  SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6,
};

/** A weekday's name in the reader's language. Index 0 = Sunday, matching cron. */
function weekdayName(index: number, locale?: string): string {
  // 2024-01-07 was a Sunday, so adding the index lands on the wanted weekday.
  const date = new Date(Date.UTC(2024, 0, 7 + index));
  return new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(date);
}

/** "9:00 AM" / "09:00" — the reader's own 12/24-hour convention, never a hardcoded format. */
export function timeLabel(hour: number, minute: number, locale?: string): string | null {
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  const date = new Date(Date.UTC(2024, 0, 1, hour, minute));
  return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }).format(date);
}

/** Expand a cron day-of-week field (single / list / range; numeric 0-7 with 7=Sun, or MON…SUN). */
function expandDow(field: string): number[] | null {
  const out = new Set<number>();
  for (const part of field.split(',')) {
    const token = part.trim().toUpperCase();
    if (!token) return null;
    const range = token.split('-');
    if (range.length > 2) return null;
    const ends = range.map((end) => {
      if (/^\d+$/.test(end)) {
        const n = Number(end);
        return n >= 0 && n <= 7 ? n % 7 : null;
      }
      return end in DOW_NAMES ? DOW_NAMES[end] : null;
    });
    if (ends.some((end) => end === null)) return null;
    const [from, to] = ends as number[];
    if (range.length === 1) {
      out.add(from);
      continue;
    }
    // A wrapping range ("FRI-MON") is legal cron; walk forwards modulo 7.
    for (let i = 0; i < 7; i += 1) {
      const day = (from + i) % 7;
      out.add(day);
      if (day === to) break;
    }
  }
  return [...out].sort((a, b) => a - b);
}

function dayDescription(dom: string, dow: string, strings: CronLabelStrings, locale?: string): string | null {
  const domAny = dom === '*';
  const dowAny = dow === '*' || dow === '?';
  if (domAny && dowAny) return strings.everyDay;

  if (!domAny && dowAny) {
    const days = dom.split(',').map((d) => Number(d.trim()));
    if (!days.length || days.some((d) => !Number.isInteger(d) || d < 1 || d > 31)) return null;
    return strings.dayOfMonth.replace('{days}', days.join(', '));
  }

  if (domAny && !dowAny) {
    const set = expandDow(dow);
    if (!set || set.length === 0) return null;
    if (set.length === 5 && set.join() === '1,2,3,4,5') return strings.weekdays;
    if (set.length === 2 && set.join() === '0,6') return strings.weekends;
    if (set.length === 1) return strings.everyDow.replace('{day}', weekdayName(set[0], locale));
    if (set.length === 7) return strings.everyDay;
    const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
    return set.map((d) => fmt.format(new Date(Date.UTC(2024, 0, 7 + d)))).join(', ');
  }

  // Both day-of-month and day-of-week constrained — not a shape our routines author, and describing it
  // wrongly is worse than falling back to the generic "Scheduled" the caller supplies.
  return null;
}

/**
 * "Every day · 9:00 AM" for a raw 5-field cron, or **null** when it cannot be described faithfully —
 * the caller then shows a generic label rather than a guess. Minute and hour must be single values
 * (routines fire at most once an hour), which is the same constraint both phones apply.
 */
export function cronLabel(cron: string | undefined, strings: CronLabelStrings, locale?: string): string | null {
  const trimmed = (cron ?? '').trim();
  if (!trimmed) return null;
  const parts = trimmed.split(/\s+/);
  if (parts.length !== 5) return null;
  if (!/^\d+$/.test(parts[0]) || !/^\d+$/.test(parts[1])) return null;
  const time = timeLabel(Number(parts[1]), Number(parts[0]), locale);
  if (!time) return null;
  const day = dayDescription(parts[2], parts[4], strings, locale);
  if (!day) return null;
  return `${day} · ${time}`;
}

/** A routine, as far as its run history is concerned. */
export interface ActivityRoutine {
  lastRun?: string | null;
  lastRunTaskCount?: number;
  runCount?: number;
  runCountCapped?: boolean;
  /** Whether the server looked far enough back to know. Absent reads as **not known**. */
  runsKnown?: boolean;
}

/**
 * What the card is allowed to say about a routine's runs.
 *
 * `unknown` is the case that matters, and it is why this is a function rather than a null check at the
 * call site. The server reads ONE shared window of run records for the whole page, so a chatty routine
 * can push a quiet one's rows out of it entirely — that routine comes back with `lastRun: null` and
 * `runCount: 0` while in fact running daily. Rendering "Hasn't run yet" there states something false
 * about the one thing this screen exists to report, so an unknown history renders NOTHING at all.
 *
 * `never` is therefore only reachable when the server actually looked and found nothing.
 */
export type RoutineActivity =
  | { kind: 'unknown' }
  | { kind: 'never' }
  | { kind: 'ran'; lastRun: string; taskCount: number; runCount: number; runCountCapped: boolean };

export function routineActivity(routine: ActivityRoutine): RoutineActivity {
  if (routine.runsKnown !== true) return { kind: 'unknown' };
  if (!routine.lastRun) return { kind: 'never' };
  return {
    kind: 'ran',
    lastRun: routine.lastRun,
    taskCount: Math.max(0, routine.lastRunTaskCount ?? 0),
    runCount: Math.max(0, routine.runCount ?? 0),
    runCountCapped: routine.runCountCapped === true,
  };
}
